import React, { useEffect, useState } from "react";
import Config from "../../config";

// Build provenance marker. On BETA it renders a VISIBLE build-info line at the bottom of the
// nav menu (tagged "only shown on beta"); on prod/stage/local it renders a hidden DOM node so
// the running build stays discoverable via dev tools (search the Elements panel for
// "build-info" or the data-build-* attributes) without showing in the UI.
// Reads /version.json (written at build time by scripts/generate-build-info.js, which runs
// as the npm `prebuild` hook on BOTH the Jenkins and GitHub Actions builds). Degrades to
// "dev" when the file isn't present/served (e.g. `npm start`, or a host that rewrites it).
function getEnvLabel() {
  const host =
    (typeof window !== "undefined" && window.location && window.location.hostname) || "";
  if (Config && Config.production) return "prod";
  if (host.indexOf("beta") > -1) return "beta";
  if (host.indexOf("stage") > -1) return "stage";
  return "local";
}

export default function BuildBadge() {
  const [info, setInfo] = useState(null);

  useEffect(() => {
    // No fetch (e.g. Jest 27 / jsdom test env, where this app uses axios and doesn't
    // polyfill fetch) -> skip and show the "dev" fallback rather than throwing.
    if (typeof fetch !== "function") return;
    let alive = true;
    const url = `${process.env.PUBLIC_URL || ""}/version.json`;
    fetch(url, { cache: "no-store" })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (alive && data && (data.commit || data.sha)) {
          setInfo(data);
        }
      })
      .catch(() => {
        /* no version.json (dev build, or host rewrote it) — show the fallback */
      });
    return () => {
      alive = false;
    };
  }, []);

  const commit = info && (info.commit || info.sha);
  const label = commit ? String(commit).slice(0, 7) : "dev";

  let built = "";
  if (info && info.builtAt) {
    const d = new Date(info.builtAt);
    if (!isNaN(d.getTime())) built = d.toISOString().slice(0, 10);
  }

  const env = getEnvLabel();
  const text = `Build ${label}${built ? ` · ${built}` : ""} · ${env}`;
  // PR number when the build was deployed from a PR — written into version.json by the CD
  // build step (REACT_APP_PR, looked up from the deployed ref). Absent for master-merge /
  // non-PR builds.
  const pr = info && info.pr ? String(info.pr) : "";

  // Provenance is always emitted as data-* attributes for dev-tools discovery.
  const dataAttrs = {
    "data-build": text,
    "data-build-commit": label,
    "data-build-date": built || "",
    "data-build-env": env,
    "data-build-pr": pr,
  };

  // BETA only: surface the build info in the menu with an explicit "only shown on beta" tag.
  if (env === "beta") {
    return (
      <div
        className="build-info build-info--beta"
        style={{
          padding: "8px 16px",
          fontSize: 12,
          lineHeight: 1.4,
          color: "#fff",
          borderTop: "1px solid rgba(255, 255, 255, 0.15)",
        }}
        {...dataAttrs}
      >
        <div style={{ fontWeight: 600 }}>{text}</div>
        {pr ? (
          <div>
            <a
              href={`https://github.com/Logicbroker/portal/pull/${pr}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#fff", textDecoration: "underline" }}
            >
              PR #{pr}
            </a>
          </div>
        ) : null}
        <div style={{ fontStyle: "italic", opacity: 0.75 }}>only shown on beta</div>
      </div>
    );
  }

  // Everywhere else: hidden, but present in the DOM and fully inspectable in dev tools.
  // `hidden` + inline display:none keeps it out of the rendered UI regardless of app CSS.
  return (
    <div className="build-info" hidden style={{ display: "none" }} {...dataAttrs}>
      {text}
    </div>
  );
}
